import React, { memo, useEffect, useState, useRef } from 'react';
import classes from './ProductSelection.module.css';
import { Box, Paper, Select, SimpleGrid, Space, Switch, Text, TextInput } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { IoAddOutline, IoTrashOutline } from "react-icons/io5";
import { baseUrl } from '../../../../../Constants/constant';
import useFetch from '../../../../../Customhooks/useFetch';
import { IoMdSearch } from 'react-icons/io';
import LocationAutocomplete from '../../../../../CustomComponent/LocationAutocomplete';

interface FormField {
    id_comp_trans: number;
    id: number | null;
    sourcelocation_id: string;
    source_place_id: string;
    transportType_id: string | null;
    destinationlocation_name: string;
    destination_place_id: string;
    distance: any;
}

interface SelectOption {
    value: string;
    label: string;
}
interface TransportProductProps {
    onTransportFieldsChange: (fields: FormField[]) => void;
    parentIndex: number;
    initialFields?: FormField[];
    showErrors?: boolean;
    computeId?: number;
}

const TransportProduct: React.FC<TransportProductProps> = ({
    onTransportFieldsChange,
    parentIndex,
    computeId,
    initialFields,
    showErrors = false
}) => {
    const [transportType, setTransportType] = useState<SelectOption[]>([]);
    const [distancePayload, setDistancePayload] = useState<any>({});
    const [shouldFetchDistance, setShouldFetchDistance] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [latLngMode, setLatLngMode] = useState<Record<number, boolean>>({});
    const [fields, setFields] = useState<FormField[]>([{
        id_comp_trans: 1,
        id: null,
        sourcelocation_id: '',
        source_place_id: '',
        transportType_id: '',
        destinationlocation_name: '',
        destination_place_id: '',
        distance: ''
    }]);

    const prevFieldsRef = useRef<string | null>(null);

    const { id } = useParams();
    const token = localStorage.getItem("token");

    const fetchConfig = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            token: token,
        },
    };

    const { data: getTransportData, fetchData: getTransport } = useFetch(fetchConfig);
    const { data: getDistanceData, loading: distanceLoading, error, fetchData: fetchDistance } = useFetch(
        {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                "Access-Control-Allow-Origin": "*",
                token: token,
            },
            body: JSON.stringify(distancePayload)
        }
    );

    // Fetch transport data only once
    useEffect(() => {
        getTransport(`${baseUrl}admin/transports`);
    }, []);

    // Process transport data when it arrives
    useEffect(() => {
        if (getTransportData?.data?.rows) {
            const TransportOptions = getTransportData?.data?.rows?.map((el: any) => ({
                value: el?.id.toString(),
                label: el?.transport_name,
            }));
            setTransportType(TransportOptions);
        }
    }, [getTransportData]);

    // Memoize initialFields to prevent unnecessary re-renders
    const memoizedFields = React.useMemo(() => {
        if (!initialFields || initialFields?.length === 0) return [];

        return initialFields?.map(field => ({
            ...field,
            id_comp_trans: field?.id_comp_trans,
            id: field?.id,
            sourcelocation_id: field?.sourcelocation_id?.toString() || "",
            destinationlocation_name: field?.destinationlocation_name?.toString() || "",
            source_place_id: field.source_place_id || "",
            transportType_id: field?.transportType_id?.toString() || null,
            destination_place_id: field.destination_place_id || "",
            distance: field.distance || "",
        }));
    }, [initialFields]);

    useEffect(() => {
        //TODO
        // @ts-ignore
        if (computeId > 0 && initialFields?.length > 0) {
            // @ts-ignore
            setFields(memoizedFields);
        }
    }, [computeId, initialFields])

    useEffect(() => {
        const fieldsString = JSON.stringify(fields);
        const stateString = fieldsString + String(showErrors);

        if (prevFieldsRef.current === stateString) {
            return;
        }

        prevFieldsRef.current = stateString;

        const debounce = setTimeout(() => {
            onTransportFieldsChange(fields);
            if (showErrors) {
                validateFields();
            }
        }, 300);

        return () => clearTimeout(debounce);
    }, [fields, showErrors]);

    const validateFields = () => {
        const newErrors: Record<string, string> = {};
        let isValid = true;

        fields.forEach((field, index) => {
            if (!field?.sourcelocation_id) {
                newErrors[`sourcelocation_id${index}`] = "Source Location is required";
                isValid = false;
            }
            if (!field?.transportType_id) {
                newErrors[`transportType_id${index}`] = "Transport Type is required";
                isValid = false;
            }
            if (!field?.destinationlocation_name) {
                newErrors[`destinationlocation_name${index}`] = "Destination location is required";
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    // Memoize handleChange to prevent unnecessary re-creation
    const handleChange = (index: number, field: keyof FormField, value: string | null) => {
        setFields(prev => {
            const newFields = [...prev];
            newFields[index] = {
                ...newFields[index],
                [field]: value
            };
            return newFields;
        });
    };

    // Handle location change with place_id
    const handleLocationChange = (index: number, field: keyof FormField, value: string | null, placeId?: string) => {
        const placeIdField = field === "sourcelocation_id" ? "source_place_id" : "destination_place_id";

        setFields(prev => {
            const newFields = [...prev];
            newFields[index] = {
                ...newFields[index],
                [field]: value || "",
                [placeIdField]: placeId || ""
            };
            return newFields;
        });
    };

    // Memoize addComponent to prevent unnecessary re-creation
    const addComponent = React.useCallback(() => {
        setErrors({});
        setFields(prev => [...prev, {
            id_comp_trans: prev.length + 1,
            id: null,
            sourcelocation_id: '',
            source_place_id: '',
            transportType_id: '',
            destinationlocation_name: '',
            destination_place_id: '',
            distance: ''
        }]);
    }, []);

    // Memoize handleDeleteComponent to prevent unnecessary re-creation
    const handleDeleteComponent = React.useCallback((index: number) => {
        if (fields?.length <= 1) {
            return;
        }

        setFields(prev => prev.filter((_, i) => i !== index));
    }, [fields?.length]);

    useEffect(() => {
        if (!shouldFetchDistance || !distancePayload) return;
        const makeApiCall = async () => {
            const url = `${baseUrl}compute/lca/distance`
            await fetchDistance(url);
            setShouldFetchDistance(false); // reset flag
        };
        makeApiCall();
    }, [shouldFetchDistance, distancePayload]);
    useEffect(() => {
        if (getDistanceData?.data && distancePayload) {
            setFields(prevFields =>
                prevFields.map((f, i) => {
                    if (
                        f.source_place_id === distancePayload.source_place_id &&
                        f.destination_place_id === distancePayload.destination_place_id &&
                        f.transportType_id === distancePayload.transportation_id
                    ) {
                        return { ...f, distance: getDistanceData.data.distance };
                    }
                    return f;
                })
            );
        }

    }, [getDistanceData]);

    const handleDistanceCalculation = async (e: any, id: number) => {
    e.preventDefault();

    const newPayload = {
        source_place_id: fields[id]?.source_place_id,
        destination_place_id: fields[id]?.destination_place_id,
        transportation_id: fields[id]?.transportType_id,
    };
    setDistancePayload(newPayload);
    setShouldFetchDistance(true);
};

    return (
        <div>
            {fields?.map((field, index) => (
                <Box ml={5} key={field?.id_comp_trans}>
                    <Box style={{ gridColumn: 'span 4', display: 'flex', justifyContent: 'flex-end' }}>
                        <Switch
                            checked={!!latLngMode[index]}
                            onChange={(e) => {
                                const checked = e.currentTarget.checked;
                                setLatLngMode(prev => ({ ...prev, [index]: checked }));
                            }}
                            label={latLngMode[index] ? "Lat Lng Input On" : "Lat Lng Input Off"}
                        />
                    </Box>
                    {fields?.length > 1 && <><Space h={"md"} /> <Box className={classes.delete_box} ><IoTrashOutline fill='red' size={18} onClick={() => handleDeleteComponent(index)} /></Box></>}
                    <SimpleGrid cols={4}>
                        <LocationAutocomplete
                            label="Source Location"
                            placeholder="Enter your source location"
                            withAsterisk
                            value={field?.sourcelocation_id}
                            onChange={(value, placeId) => handleLocationChange(index, "sourcelocation_id", value, placeId)}
                            error={showErrors ? errors[`sourcelocation_id${index}`] : null}
                            latLngMode={!!latLngMode[index]}
                        />
                        <TextInput
                            style={{ display: 'none' }}
                            value={field.source_place_id}
                            onChange={(e) => handleChange(index, "source_place_id", e.target.value)}
                        />
                        <LocationAutocomplete
                            label="Destination"
                            placeholder="Enter your destination location"
                            withAsterisk
                            value={field?.destinationlocation_name}
                            onChange={(value, placeId) => handleLocationChange(index, "destinationlocation_name", value, placeId)}
                            error={showErrors ? errors[`destinationlocation_name${index}`] : null}
                            latLngMode={!!latLngMode[index]}
                        />
                        <TextInput
                            style={{ display: 'none' }}
                            value={field.destination_place_id}
                            onChange={(e) => handleChange(index, "destination_place_id", e.target.value)}
                        />
                        <Select
                            label="Transport Type"
                            withAsterisk
                            variant="filled"
                            placeholder="Choose Transport Type"
                            data={transportType}
                            searchable
                            maxDropdownHeight={200}
                            value={field?.transportType_id}
                            comboboxProps={{ withinPortal: false, shadow: 'md' }}
                            onChange={(value) => handleChange(index, "transportType_id", value)}
                            radius={6}
                            error={showErrors ? errors[`transportType_id${index}`] : null}
                        />
                        <TextInput
                            label="Distance"
                            variant="filled"
                            placeholder="Distance"
                            value={field.distance}
                            readOnly
                            disabled
                            rightSection={
                                <Box onClick={(e) => handleDistanceCalculation(e, index)} className={classes.searchBox}>
                                    <IoMdSearch size={18} color="#fff" />
                                </Box>
                            }
                        />
                        <TextInput
                            value={field?.id_comp_trans}
                            onChange={(e) => handleChange(index, "id", e.target.value)}
                            style={{ display: 'none' }}
                        />
                    </SimpleGrid>
                </Box>
            ))}
            <Space h="md" />
            <Box className={classes.addmoreleg}>
                <span style={{ display: 'flex' }}><IoAddOutline size={18} /></span>
                <Text
                    variant="default"
                    onClick={addComponent}
                    ta="right"
                    pl={4}
                    color='#404040;'
                    size='sm'
                >
                    Add More Leg
                </Text>
            </Box>
        </div>
    );
};

export default memo(TransportProduct);